import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  TrendingUp, 
  DollarSign, 
  Home,
  Calendar,
  PieChart,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Percent
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  getRevenueAnalytics, 
  getOccupancyAnalytics, 
  getProfitAnalytics,
  getBookingSourcesAnalytics 
} from '@/lib/api';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const SOURCE_LABELS = {
  internal: 'Direct Booking',
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  evolve: 'Evolve',
  'booking.com': 'Booking.com',
  google: 'Google Calendar',
  microsoft: 'Microsoft Calendar',
  other: 'Other'
};

export const AnalyticsView = ({ selectedProperty, properties }) => {
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [revenueData, setRevenueData] = useState(null);
  const [occupancyData, setOccupancyData] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [sourcesData, setSourcesData] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const propertyId = selectedProperty?.id || null;
      
      const [revenue, occupancy, profit, sources] = await Promise.all([
        getRevenueAnalytics(propertyId, year),
        getOccupancyAnalytics(propertyId, year),
        getProfitAnalytics(propertyId, year),
        getBookingSourcesAnalytics(propertyId, year)
      ]);
      
      setRevenueData(revenue.data);
      setOccupancyData(occupancy.data);
      setProfitData(profit.data);
      setSourcesData(sources.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, year]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Transform data for charts
  const monthlyRevenueChart = revenueData?.revenue_by_month 
    ? Object.entries(revenueData.revenue_by_month).map(([month, value]) => ({
        month: month.slice(5), // MM format
        revenue: value
      }))
    : [];

  const profitByMonthChart = profitData?.profit_by_month
    ? Object.entries(profitData.profit_by_month).map(([month, data]) => ({
        month: month.slice(5),
        revenue: data.revenue,
        expenses: data.expenses,
        profit: data.profit
      }))
    : [];

  const propertyRevenueChart = revenueData?.revenue_by_property
    ? Object.entries(revenueData.revenue_by_property).map(([name, value]) => ({
        name: name.length > 15 ? name.slice(0, 15) + '...' : name,
        fullName: name,
        value
      }))
    : [];

  const expensesCategoryChart = profitData?.expenses_by_category
    ? Object.entries(profitData.expenses_by_category).map(([category, value]) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1),
        value
      }))
    : [];

  const sourcesChart = sourcesData?.sources
    ? Object.entries(sourcesData.sources).map(([source, data]) => ({
        name: SOURCE_LABELS[source] || source,
        bookings: data.count,
        revenue: data.revenue,
        percentage: data.percentage
      }))
    : [];

  const occupancyByPropertyChart = occupancyData?.occupancy_by_property
    ? Object.entries(occupancyData.occupancy_by_property).map(([name, data]) => ({
        name: name.length > 15 ? name.slice(0, 15) + '...' : name,
        fullName: name,
        rate: data.occupancy_rate,
        nights: data.booked_nights
      }))
    : [];

  const years = [2024, 2025, 2026, 2027];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Revenue Analytics
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Comprehensive financial overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics} data-testid="refresh-analytics-btn">
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-100" data-testid="total-revenue-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  ${(revenueData?.total_revenue || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100" data-testid="total-expenses-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Expenses</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  ${(profitData?.total_expenses || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <ArrowDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100" data-testid="net-profit-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Net Profit</p>
                <p className={`text-2xl font-bold mt-1 ${(profitData?.total_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  ${(profitData?.total_profit || 0).toLocaleString()}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${(profitData?.total_profit || 0) >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {(profitData?.total_profit || 0) >= 0 
                  ? <ArrowUp className="h-6 w-6 text-emerald-600" />
                  : <ArrowDown className="h-6 w-6 text-red-600" />
                }
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100" data-testid="occupancy-rate-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Occupancy Rate</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {occupancyData?.overall_occupancy_rate || 0}%
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Percent className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profit Margin Card */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-testid="profit-margin-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-300 text-sm">Overall Profit Margin</p>
              <p className="text-4xl font-bold mt-2">{profitData?.overall_margin || 0}%</p>
              <p className="text-slate-400 text-sm mt-1">
                Based on ${(revenueData?.total_revenue || 0).toLocaleString()} revenue
              </p>
            </div>
            <TrendingUp className="h-16 w-16 text-slate-600" />
          </div>
        </CardContent>
      </Card>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue & Profit Chart */}
        <Card className="bg-white border border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-slate-600" />
              Monthly Revenue vs Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="monthly-revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={profitByMonthChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" name="Revenue" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Property */}
        <Card className="bg-white border border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Home className="h-5 w-5 text-slate-600" />
              Revenue by Property
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="property-revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={propertyRevenueChart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expenses by Category */}
        <Card className="bg-white border border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <PieChart className="h-5 w-5 text-slate-600" />
              Expenses by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="expenses-category-chart">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={expensesCategoryChart}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {expensesCategoryChart.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Booking Sources */}
        <Card className="bg-white border border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-600" />
              Booking Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="booking-sources-chart">
              {sourcesChart.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={sourcesChart}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="bookings"
                      label={({ name, percentage }) => `${name} ${percentage}%`}
                      labelLine={false}
                    >
                      {sourcesChart.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value} bookings ($${props.payload.revenue.toLocaleString()})`,
                        props.payload.name
                      ]} 
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  No booking data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Occupancy by Property */}
      <Card className="bg-white border border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Percent className="h-5 w-5 text-slate-600" />
            Occupancy Rate by Property
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64" data-testid="occupancy-by-property-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={occupancyByPropertyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'rate' ? `${value}%` : `${value} nights`,
                    name === 'rate' ? 'Occupancy Rate' : 'Booked Nights'
                  ]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="rate" fill="#8b5cf6" name="Occupancy Rate" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Profit by Property Table */}
      <Card className="bg-white border border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Profit by Property</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto" data-testid="profit-by-property-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Property</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Revenue</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Expenses</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Profit</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Margin</th>
                </tr>
              </thead>
              <tbody>
                {profitData?.profit_by_property && Object.entries(profitData.profit_by_property).map(([name, data]) => (
                  <tr key={name} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">{name}</td>
                    <td className="py-3 px-4 text-right text-emerald-600">${data.revenue.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-red-600">${data.expenses.toLocaleString()}</td>
                    <td className={`py-3 px-4 text-right font-medium ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${data.profit.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        data.margin >= 50 ? 'bg-emerald-100 text-emerald-700' :
                        data.margin >= 20 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {data.margin}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!profitData?.profit_by_property || Object.keys(profitData.profit_by_property).length === 0) && (
              <p className="text-center text-slate-500 py-8">No profit data available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsView;
